import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { t, fmtHours, fmtDay } from "../helpers/format"
import { dayKey, isApprovable } from "../helpers/states"
import Cell from "./Cell"
import Checkbox from "./Checkbox"
import PersonAvatar from "./PersonAvatar"

const GridRow = ({ row, days, mode, selected, flash, active, busy, onToggle, onOpen, onApproveRow }) => {
  const { user, cells, counters } = row
  const keys        = days.filter(day => isApprovable(cells[day.date])).map(day => dayKey(user.id, day.date))
  const allChecked  = keys.length > 0 && keys.every(key => selected.has(key))
  const someChecked = keys.some(key => selected.has(key))
  const total       = days.reduce((sum, day) => sum + (cells[day.date]?.hours || 0), 0)
  const overtime    = (counters.overtime_25 || 0) + (counters.overtime_50 || 0)
  const isActiveRow = active?.user?.id === user.id

  return (
    <div className="contents group" role="row">
      <div
        className = {clsx(
          "sticky left-0 z-10 flex items-center gap-2.5 px-3 py-2 border-b border-r border-slate-100 transition-colors",
          isActiveRow ? "bg-brand-50/60" : "bg-white group-hover:bg-slate-50"
        )}
      >
        <Checkbox
          checked       = {allChecked}
          indeterminate = {!allChecked && someChecked}
          disabled      = {!keys.length}
          onChange      = {on => onToggle(keys, on)}
          label         = {t("grid.select_row", { name: user.name })}
        />
        <PersonAvatar user={user} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-slate-900 truncate" title={user.name}>{user.name}</div>
          <div className="text-[11px] text-slate-400 truncate">
            {user.team ? t("grid.team", { name: user.team }) : t("grid.no_team")}
          </div>
        </div>
        {keys.length > 0 && (
          <button
            type      = "button"
            disabled  = {busy}
            onClick   = {() => onApproveRow(keys)}
            title     = {t("actions.approve_week_hint", { count: keys.length })}
            className = "absolute right-2 top-1/2 -translate-y-1/2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 inline-flex items-center gap-1 h-[24px] px-2 rounded-md text-[11px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100 transition-opacity disabled:opacity-40"
          >
            <FontAwesomeIcon icon="check-double" className="text-[10px]" />
            {mode === "month" ? t("actions.approve_month") : t("actions.approve_week")}
          </button>
        )}
      </div>

      {days.map(day => {
        const key = dayKey(user.id, day.date)
        return (
          <Cell
            key        = {day.date}
            day        = {day}
            cell       = {cells[day.date]}
            mode       = {mode}
            selected   = {selected.has(key)}
            selectable = {isApprovable(cells[day.date])}
            flash      = {flash.has(key)}
            active     = {isActiveRow && active.date === day.date}
            onToggle   = {on => onToggle([key], on)}
            onOpen     = {() => onOpen(user, day.date)}
          />
        )
      })}

      <div className="flex flex-col items-end justify-center gap-0.5 px-3 py-2 border-b border-l border-slate-100 bg-white group-hover:bg-slate-50 transition-colors">
        {row.weeks_over?.length > 0 ? (
          <span
            className = "inline-flex items-center gap-1 text-[14px] font-bold tabular-nums text-orange-600"
            title     = {row.weeks_over.map(week => t("grid.over_week", { hours: fmtHours(week.hours), date: fmtDay(week.week, { day: "numeric", month: "long" }) })).join("\n")}
          >
            <FontAwesomeIcon icon="triangle-exclamation" className="text-[11px]" />
            {fmtHours(total)}
          </span>
        ) : (
          <span className="text-[14px] font-bold tabular-nums text-slate-900">{fmtHours(total)}</span>
        )}
        {overtime > 0 && (
          <span className="text-[10.5px] font-semibold tabular-nums text-amber-600" title={t("grid.overtime_detail", { h25: fmtHours(counters.overtime_25), h50: fmtHours(counters.overtime_50) })}>
            {t("grid.overtime_short", { hours: fmtHours(overtime) })}
          </span>
        )}
        {counters.meals > 0 && (
          <span className="inline-flex items-center gap-1 text-[10.5px] tabular-nums text-slate-500" title={t("grid.meals", { count: counters.meals })}>
            <FontAwesomeIcon icon="utensils" className="text-[9px] text-slate-400" />
            {counters.meals}
          </span>
        )}
      </div>
    </div>
  )
}

export default GridRow
